import { useState, useEffect } from "react";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const ChangePasswordForm = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [strength, setStrength] = useState("");

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  // Password strength check
  useEffect(() => {
    if (!newPassword) {
      setStrength("");
      return;
    }
    const strongRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const mediumRegex =
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&]).{6,}$/;

    if (strongRegex.test(newPassword)) {
      setStrength("Strong");
    } else if (mediumRegex.test(newPassword)) {
      setStrength("Medium");
    } else {
      setStrength("Weak");
    }
  }, [newPassword]);

  const handleChange = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");

    if (newPassword !== confirmPassword) {
      setError("❌ New passwords do not match.");
      return;
    }

    if (!token) {
      setError("❌ Token missing. Please log in again.");
      return;
    }

    try {
      await axios.put(
        `${API_BASE}/api/admin/profile/change-password`,
        { oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMsg("✅ Password updated successfully.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStrength("");
    } catch (err) {
      console.error("Password update failed:", err);
      setError("❌ Password change failed. Please try again.");
    }
  };

  const inputClasses =
    "w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600";

  const strengthColor = {
    Weak: "text-red-500",
    Medium: "text-yellow-500",
    Strong: "text-green-500",
  };

  return (
    <form
      onSubmit={handleChange}
      className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md space-y-4"
    >
      <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-gray-100">
        🔐 Change Password
      </h3>

      {msg && <p className="text-green-500 text-sm">{msg}</p>}
      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Old Password */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
          Old Password
        </label>
        <div className="relative">
          <input
            type={showOld ? "text" : "password"}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className={inputClasses}
            required
          />
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-600 dark:text-gray-300"
            onClick={() => setShowOld((prev) => !prev)}
          >
            {showOld ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {/* New Password */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
          New Password
        </label>
        <div className="relative">
          <input
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClasses}
            required
          />
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-600 dark:text-gray-300"
            onClick={() => setShowNew((prev) => !prev)}
          >
            {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {newPassword && (
          <p className={`text-sm mt-1 ${strengthColor[strength]}`}>
            Strength: {strength}
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
          Confirm New Password
        </label>
        <div className="relative">
          <input
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClasses}
            required
          />
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-600 dark:text-gray-300"
            onClick={() => setShowConfirm((prev) => !prev)}
          >
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {confirmPassword && newPassword !== confirmPassword && (
          <p className="text-red-500 text-sm mt-1">❌ Passwords do not match</p>
        )}
      </div>

      <button
        type="submit"
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded mt-2 transition w-full"
      >
        Update Password
      </button>
    </form>
  );
};

export default ChangePasswordForm;
